import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export default function Searchbar({ placeholder, onSearchSubmit }) {
  const { i18n } = useTranslation();

  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const wrapperRef = useRef(null);

  const handleChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    if (debounceTimeout) clearTimeout(debounceTimeout);

    const timeout = setTimeout(() => {
      fetchSuggestions(value);
    }, 500); // debounce time

    setDebounceTimeout(timeout);
  };

  const fetchSuggestions = async (value) => {
    if (!value.trim()) return setSuggestions([]);

    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/main/search/suggestions`, {
        params: { q: value, lang: i18n.language }
      });
      setSuggestions(res.data);
    } catch (err) {
      console.error('Error fetching suggestions', err);
    }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (search.trim() && typeof onSearchSubmit === 'function') {
      onSearchSubmit(search);
    }
    setSuggestions([]);
  };
  

  // const handleSuggestionClick = (suggestion) => {
  //   setSearch(suggestion);
  //   setSuggestions([]);
  //   if (typeof onSearchSubmit === 'function') {
  //     onSearchSubmit(suggestion);
  //   }
  // };


  const logSuggestionClick = async (suggestion, searchTerm) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/main/search/click`, {
        itemId: suggestion._id,
        name: suggestion.name,
        arabicName: suggestion.arabicName,
        link: suggestion.link,
        store: suggestion.store,
        searchTerm,
        lang: i18n.language
      }, 
      // { withCredentials: true }
    ); // includes cookies if auth token is in cookies
    } catch (err) {
      console.error('Error logging search click:', err);
    }
  };

  
  const handleSuggestionClick = async (suggestion) => {
    setSearch(suggestion.name || suggestion.arabicName); // Set search bar value to the clicked suggestion's name
    setSuggestions([]); // Clear suggestions

    await logSuggestionClick(suggestion, search); // log the click before redirecting

    // Navigate to the product page based on _id
    if (suggestion._id) {
      window.location.href = `/product-page/${suggestion._id}`;
    }
  };
  

  const clearSearch = () => {
    setSearch('');
    setSuggestions([]);
  };

  // Detect clicks outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="sticky top-0 z-40 bg-base-100 shadow-sm px-4 py-3 dark:bg-white">
      <div
        className="max-w-3xl mx-auto flex flex-col gap-2 relative dark:bg-white"
        ref={wrapperRef}
      >
        <form onSubmit={handleSubmit} className="flex items-center gap-3 relative w-full dark:bg-white">
          <input
            type="text"
            placeholder={placeholder}
            className="rounded-xl input input-bordered w-full pr-10 focus:outline-none focus:ring-1 focus:ring-[#FF9900] focus:border-[#FF9900] dark:text-white dark:bg-slate-600"
            value={search}
            onChange={handleChange}
          />
          {search && (
            <button
              type="button"
              onClick={clearSearch}
              // className="absolute right-16 text-gray-400 hover:text-gray-600"
              className={`absolute ${i18n.language === 'ar' ? 'left-16' : 'right-16'} text-gray-400 hover:text-gray-600`}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
          <button type="submit" className="rounded-xl btn bg-[#FF9900] text-white px-4 py-2">
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </form>

        {suggestions?.length > 0 && (
          <ul className="absolute top-full mt-1 bg-white shadow rounded w-full z-50 max-h-48 overflow-y-auto border border-gray-200">
            {suggestions.map((item, i) => (
              <li
                key={item._id || i}
                onClick={() => handleSuggestionClick(item)}
                className="px-4 py-2 cursor-pointer hover:bg-gray-100"
              >
                {item.name || item.arabicName}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
